import { useCallback, useEffect, useRef } from 'react';
import { KeyStreamAssembler, ScanInputHub, type AssembleOutcome, type ScanEvent } from '@wms/scan-input';
import { DEFAULT_SCAN_CONFIG, isTypingTarget, keyToChar, type ScanConfig } from './scanCore';

export type ScanSource = 'wedge' | 'field';

export type UseScannerOptions = {
  /** Scan mode on/off. Off: no listener at all. */
  readonly enabled: boolean;
  /** One call per accepted scan (never for a duplicate). */
  readonly onScan: (code: string, source: ScanSource) => void;
  /** The same ScanEvent the shared layer produced (source KEYBOARD, provider browser-keyboard). */
  readonly onScanEvent?: (event: ScanEvent) => void;
  /** The same code again within the duplicate window: shown as a notice, not counted. */
  readonly onDuplicate?: (code: string) => void;
  readonly config?: Partial<ScanConfig>;
  /** Time of a key event in ms. Default `event.timeStamp` (when the key happened, not when the page got round to it). */
  readonly timeOf?: (e: KeyboardEvent) => number;
  /** Wall clock for the duplicate window (tests inject one). */
  readonly now?: () => number;
};

const PROVIDER_ID = 'browser-keyboard';

/**
 * Framework-free scanner capture as a hook (browser keyboard-wedge provider). While `enabled`, a keyboard-wedge
 * scanner (USB / Bluetooth HID) works anywhere on the page - no field needs focus:
 *
 *  - keystrokes are collected by the shared `KeyStreamAssembler`; a fast run ended by Enter or Tab is ONE scan,
 *    and its Enter is swallowed so it cannot submit a form or press a focused button;
 *  - a fast run with no suffix at all is committed after a short silence (scanners configured without one);
 *  - keystrokes aimed at a field the person is typing into are never touched (the scan field has its own
 *    handler: `submit` is what it calls), and slow typing is never a scan;
 *  - every scan goes through the shared hub: normalisation, flood guard and duplicate suppression are the same
 *    code the Android app runs, and a scan arrives as one `ScanEvent`.
 *
 * Ops cannot receive an Android Intent/broadcast: a device whose scanner only speaks Intent must use the TSD app.
 */
export function useScanner(opts: UseScannerOptions): { readonly submit: (code: string, source: ScanSource) => boolean } {
  const optsRef = useRef(opts);
  optsRef.current = opts;

  const engineRef = useRef<{ hub: ScanInputHub; assembler: KeyStreamAssembler } | null>(null);
  if (engineRef.current === null) {
    const hub = new ScanInputHub({
      mode: 'KEYBOARD',
      dedupe: { sameProviderMs: DEFAULT_SCAN_CONFIG.duplicateWindowMs, crossProviderMs: 0 },
      now: () => (optsRef.current.now ?? Date.now)(),
    });
    const assembler = new KeyStreamAssembler(DEFAULT_SCAN_CONFIG);
    hub.subscribe((event) => {
      const o = optsRef.current;
      const source: ScanSource = event.provider.via === 'FIELD' ? 'field' : 'wedge';
      o.onScanEvent?.(event);
      o.onScan(event.value, source);
    }, 'ops');
    engineRef.current = { hub, assembler };
  }
  const { hub, assembler } = engineRef.current;

  const config = (): ScanConfig => ({ ...DEFAULT_SCAN_CONFIG, ...optsRef.current.config });

  const deliver = useCallback((raw: string, via: string, maxGapMs?: number): boolean => {
    const cfg = { ...DEFAULT_SCAN_CONFIG, ...optsRef.current.config };
    hub.setDedupe({ sameProviderMs: cfg.duplicateWindowMs, crossProviderMs: 0 });
    const decision = hub.accept({ value: raw, source: 'KEYBOARD', providerId: PROVIDER_ID, via, ...(maxGapMs !== undefined ? { maxGapMs } : {}), targetId: 'ops' });
    if (!decision.accepted && decision.reason === 'DUPLICATE_SAME_PROVIDER') {
      const text = typeof raw === 'string' ? raw.trim() : '';
      if (text !== '') optsRef.current.onDuplicate?.(text);
    }
    return decision.accepted;
  }, [hub]);

  const submit = useCallback((raw: string, source: ScanSource): boolean => deliver(raw, source === 'field' ? 'FIELD' : 'WEDGE'), [deliver]);

  useEffect(() => {
    if (!opts.enabled) {
      assembler.reset();
      return undefined;
    }
    let idleTimer: ReturnType<typeof setTimeout> | null = null;
    let lastEventTime = 0;
    const clearIdle = (): void => {
      if (idleTimer !== null) {
        clearTimeout(idleTimer);
        idleTimer = null;
      }
    };
    const handle = (out: AssembleOutcome | null): boolean => {
      if (out === null || out.kind !== 'SCAN') return false;
      deliver(out.value, out.via === 'IDLE' ? 'IDLE' : 'WEDGE', out.maxGapMs);
      return true;
    };
    const scheduleIdle = (): void => {
      clearIdle();
      const cfg = config();
      if (cfg.idleCommitMs === null || assembler.length === 0) return;
      idleTimer = setTimeout(() => {
        idleTimer = null;
        assembler.setConfig(config());
        // The timer is re-armed by every character, so firing means no key arrived for at least idleCommitMs.
        handle(assembler.tick(lastEventTime + cfg.idleCommitMs! + 10));
      }, cfg.idleCommitMs + 10);
    };

    const onKeyDown = (e: KeyboardEvent): void => {
      const o = optsRef.current;
      if (e.isComposing || e.defaultPrevented) return;
      if (isTypingTarget(e.target)) {
        assembler.reset();
        clearIdle();
        return;
      }
      assembler.setConfig(config());
      const t = (o.timeOf ?? ((ev: KeyboardEvent) => ev.timeStamp))(e);
      lastEventTime = t;
      if (e.key === 'Enter' || e.key === 'Tab') {
        clearIdle();
        const out = assembler.terminate(t, e.key === 'Enter' ? 'ENTER' : 'TAB');
        if (out.kind === 'SCAN') {
          // This keystroke belongs to the scanner: no form submit, no button press, no focus jump.
          e.preventDefault();
          e.stopPropagation();
          handle(out);
        }
        return;
      }
      const c = keyToChar(e);
      if (c === null) return;
      assembler.pushChar(c, t);
      // Once it is clearly a burst, stop the characters doing their usual thing (space on a button, quick-find...).
      if (assembler.length >= 3) e.preventDefault();
      scheduleIdle();
    };
    window.addEventListener('keydown', onKeyDown, true);
    return () => {
      window.removeEventListener('keydown', onKeyDown, true);
      clearIdle();
      assembler.reset();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [opts.enabled, assembler, deliver]);

  return { submit };
}
